'use client'

import { motion } from 'framer-motion'
import { useAtom, useSetAtom } from 'jotai'
import {
  ArrowLeftRightIcon,
  CheckIcon,
  ChevronDownIcon,
  CrosshairIcon,
  FileQuestionIcon,
  HourglassIcon,
  LayersIcon,
  ShieldIcon,
  SlidersHorizontalIcon,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useContext, useEffect, useMemo, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { WIDGET_MENU_ROW_CLASS, WidgetMenuRowContent } from '@/components/widget/widget-menu-row'
// import { useOpenArbDialog } from '@/features/arb/hooks/use-open-arb-dialog'
import { PlanLockTooltip, PlanStar } from '@/features/billing/components/plan-lock-tooltip'
// import { useOpenHedgeDialog } from '@/features/hedge/hooks/use-open-hedge-dialog'
import { ChatContainerContext } from '@/features/order/chat/lib/chat-container-context'
import { PromptFillFlash } from '@/features/order/order-form/components/widget/prompt-fill-flash'
import { useOrderFormCaps } from '@/features/order/order-form/hooks/use-order-form-caps'
import {
  ORDER_TAB_ALGO,
  useOrderTabLabel,
} from '@/features/order/order-form/hooks/use-order-tab-lock'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { resolveOrderTabs, snapTab } from '@/features/order/order-form/lib/order-tab-caps'
import type { DiagramId } from '@/features/tutorial/lib/tutorials'
import { useCapabilities } from '@/hooks/use-capabilities'
import { useUpgradeHint } from '@/hooks/use-upgrade-hint'
import { isAlgoAllowed as capsAllowAlgo } from '@/lib/billing/capabilities'
// import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
// import { parseExchangeField } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
// import { accountsForVenue, isOrderEligibleAccount } from '@/lib/utils/account-select'
// import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
// import { useUserAccounts } from '@/service/account.hook'
import { OrderFormSummaryTab, orderFormSummaryTabAtomFamily } from '@/state/order'
import { tutorialRequestAtom } from '@/state/tutorial'
import { AlgoType } from '@/types/generated/algo'

// The tutorial a plan-locked order type opens as its "here's what you'd get" preview.
const TAB_TUTORIAL: Partial<Record<OrderFormSummaryTab, DiagramId>> = {
  [OrderFormSummaryTab.TWAP]: 'twap',
  [OrderFormSummaryTab.AdvancedTwap]: 'advanced-twap',
  [OrderFormSummaryTab.LimitChase]: 'limit-chase',
  [OrderFormSummaryTab.RFQ]: 'rfq',
}

// Locked dropdown items stay clickable (they open the preview), so they dim without `disabled`.
const LOCKED_ITEM = 'opacity-50'

const TAB_DETAILS = {
  [OrderFormSummaryTab.TWAP]: { icon: HourglassIcon, description: 'twap' },
  [OrderFormSummaryTab.AdvancedTwap]: { icon: SlidersHorizontalIcon, description: 'advancedTwap' },
  [OrderFormSummaryTab.LimitChase]: { icon: CrosshairIcon, description: 'limitChase' },
  [OrderFormSummaryTab.Scale]: { icon: LayersIcon, description: 'scale' },
  [OrderFormSummaryTab.RFQ]: { icon: FileQuestionIcon, description: 'rfq' },
  [OrderFormSummaryTab.SingleHedge]: { icon: ShieldIcon, description: 'singleHedge' },
} as const

export default function TabFormType() {
  const t = useTranslations('orderForm')
  const descriptions = useTranslations('orderForm.proDescriptions')
  const formContext = useUnifiedOrderFormContext()
  const [selectedTab, setSelectedTab] = useAtom(orderFormSummaryTabAtomFamily(formContext.cardId))
  // What every row of this card's venues can place — not the global exchangeAtom, so the tabs
  // match the venues the order will actually submit to.
  const { caps, fromServer } = useOrderFormCaps(formContext.cardId)
  const {
    primary: primaryTabs,
    dropdown: dropdownTabs,
    spreader: supportsSpreader,
  } = useMemo(() => resolveOrderTabs(caps), [caps])

  const { isAlgoAllowed } = useCapabilities()
  const isDisabled = (tab: OrderFormSummaryTab) => !isAlgoAllowed(ORDER_TAB_ALGO[tab])
  // const openHedgeDialog = useOpenHedgeDialog()
  // const openArbDialog = useOpenArbDialog()
  // const isArbAllowed = isAlgoAllowed(AlgoType.ALGO_TYPE_CROSS_EXCHANGE_ARB)
  const isSpreaderAllowed = isAlgoAllowed(AlgoType.ALGO_TYPE_SPREADER)
  const upgradeHint = useUpgradeHint()
  const planFor = (algo: AlgoType) => upgradeHint((caps) => capsAllowAlgo(caps, algo))
  const setTutorial = useSetAtom(tutorialRequestAtom)
  const previewLocked = (id: DiagramId) => {
    setDropdownOpen(false)
    setTutorial({ id, source: 'upsell' })
  }

  // Up to the first two order-form rows become the arb builder's leg1/leg2 seed —
  // a single selected instrument seeds leg1 only, matching how the dialog's own
  // "+" entry point (Active tab) leaves it blank instead of guessing a leg2.
  // const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  // const { data: userAccountsData } = useUserAccounts()
  // const arbEligibleAccounts = useMemo(
  //   () => (userAccountsData?.accounts ?? []).filter(isOrderEligibleAccount),
  //   [userAccountsData?.accounts]
  // )
  // const items = formContext.form.watch('items')
  // const arbSeedLegs = useMemo(
  //   () =>
  //     (items ?? [])
  //       .filter((item): item is NonNullable<typeof item> & { symbol: string } => !!item?.symbol)
  //       .slice(0, 2)
  //       .map((item) => {
  //         // A row's own accountId (not every creation path sets one) wins when it is
  //         // still order-eligible.
  //         // Otherwise resolve an account from the row's OWN exchange — never fall
  //         // back to this card's single shared account for every leg, or two rows
  //         // on different venues (the whole point of a cross-exchange arb) silently
  //         // collapse onto whichever one exchange the card itself happens to be on.
  //         if (
  //           item.accountId &&
  //           arbEligibleAccounts.some((account) => account.accountId === item.accountId)
  //         ) {
  //           return { accountId: item.accountId, symbol: item.symbol }
  //         }
  //         const itemExchange = parseExchangeField(item.exchange, primaryExchange)
  //         const venue = exchangeToTradeVenue(itemExchange)
  //         const matched = accountsForVenue(arbEligibleAccounts, venue)[0]
  //         // No cross-venue card fallback: an unresolved leg stays empty and the arb
  //         // dialog validation requires an explicit eligible account.
  //         return { accountId: matched?.accountId ?? '', symbol: item.symbol }
  //       }),
  //   [items, primaryExchange, arbEligibleAccounts]
  // )

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const chatContainer = useContext(ChatContainerContext)
  const portalContainer = chatContainer?.ownerDocument.body

  // A persisted tab can outlive a switch to a venue that can't place it — snap back to a
  // standard order type. Saved algo tabs wait for server caps; basic order types can snap
  // immediately because the fallback already declares whether Market/Limit are supported.
  useEffect(() => {
    const next = snapTab(selectedTab, caps, fromServer)
    if (next !== selectedTab) setSelectedTab(next)
  }, [caps, fromServer, selectedTab, setSelectedTab])

  // A locked tab can still be the selected one — a prompt-drafted TWAP, a persisted selection, a
  // plan downgrade. It is kept (never silently swapped for another order type with the same size);
  // the submit button is what locks, see useOrderTabLock in order-form-summary.tsx.

  // Spreader mode is entered orthogonally to `selectedTab` — a spread-matrix
  // widget cell click tags two legs with `spreaderRole` directly (see
  // `hasSpreaderLegs`/`isSpreaderMode` in use-unified-order-form.ts),
  // regardless of whichever order-type tab was last active. Picking a normal
  // tab while spreader-eligible just dismisses it (leaves the legs/tags
  // alone) so the "Spreader" item in the Pro dropdown can switch back later.
  const selectTab = (tab: OrderFormSummaryTab) => {
    if (formContext.isSpreaderMode) formContext.setSpreaderDismissed(true)
    if (tab === selectedTab) return
    setSelectedTab(tab)
  }

  const isProSelected =
    !formContext.isSpreaderMode && dropdownTabs.some((tab) => tab === selectedTab)
  const tabLabel = useOrderTabLabel()

  return (
    // Sticky to the order form's scroll area so the order type stays switchable from anywhere
    // in a long ticket. Above the order rows (z-index 30 - index) so the blur samples them.
    <PromptFillFlash
      cardId={formContext.cardId}
      area={{ kind: 'tab' }}
      className="sticky top-0 z-40 rounded-none bg-white/3 backdrop-blur-md"
    >
      <div data-tutorial="types" className="w-full">
        <Tabs
          // No real value matches while spreader mode's own indicator is showing,
          // so Limit/Market don't ALSO render as active alongside it.
          value={formContext.isSpreaderMode ? '' : selectedTab}
          onValueChange={(value) => selectTab(value as OrderFormSummaryTab)}
        >
          <TabsList className="flex w-full border-b border-white/10">
            {primaryTabs.map((tab) => (
              <PlanLockTooltip
                key={tab}
                locked={isDisabled(tab)}
                feature={tabLabel(tab)}
                plan={planFor(ORDER_TAB_ALGO[tab])}
                className="flex min-w-0 flex-1"
              >
                <TabsTrigger className="min-w-0" value={tab} disabled={isDisabled(tab)}>
                  {tabLabel(tab)}
                  {isDisabled(tab) && <PlanStar />}
                </TabsTrigger>
              </PlanLockTooltip>
            ))}

            {(dropdownTabs.length > 0 || supportsSpreader) && (
              <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'inline-flex h-full min-w-0 flex-1 cursor-pointer items-center justify-center gap-1.5 border-b px-2 py-3 font-sans text-sm font-semibold tracking-tight whitespace-nowrap disabled:pointer-events-none disabled:opacity-50',
                      formContext.isSpreaderMode || isProSelected
                        ? 'border-b-foreground text-white'
                        : 'border-b-transparent text-white/40',
                      dropdownOpen && 'bg-foreground/5'
                    )}
                  >
                    <span>
                      {formContext.isSpreaderMode
                        ? t('spreader')
                        : isProSelected
                          ? tabLabel(selectedTab)
                          : t('pro')}
                    </span>
                    {isProSelected && isDisabled(selectedTab) && <PlanStar />}
                    <ChevronDownIcon
                      size={12}
                      className={cn('shrink-0 transition-transform', dropdownOpen && 'rotate-180')}
                    />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="no-drag max-h-[var(--radix-popover-content-available-height)] w-60 max-w-[calc(100vw-2rem)] overflow-y-auto p-0"
                  align="end"
                  container={portalContainer}
                >
                  <div className="flex flex-col">
                    {/* Spreader can start from an empty ticket. Existing legs are assigned
                    quoting/taking roles by activateSpreader; otherwise the add-leg picker
                    builds the pair starting with a quoting leg. */}
                    {supportsSpreader && (
                      <PlanLockTooltip
                        locked={!isSpreaderAllowed}
                        feature={t('spreader')}
                        plan={planFor(AlgoType.ALGO_TYPE_SPREADER)}
                        className="flex flex-col"
                      >
                        <motion.button
                          type="button"
                          onClick={() => {
                            if (!isSpreaderAllowed) return previewLocked('spreader-algo')
                            formContext.activateSpreader()
                            setDropdownOpen(false)
                          }}
                          className={cn(WIDGET_MENU_ROW_CLASS, !isSpreaderAllowed && LOCKED_ITEM)}
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: 0.15 }}
                        >
                          <WidgetMenuRowContent
                            icon={<ArrowLeftRightIcon size={18} />}
                            label={
                              <>
                                {t('spreader')}
                                {!isSpreaderAllowed && <PlanStar />}
                              </>
                            }
                            description={descriptions('spreader')}
                            trailing={
                              formContext.isSpreaderMode ? (
                                <CheckIcon size={14} className="shrink-0" />
                              ) : null
                            }
                          />
                        </motion.button>
                      </PlanLockTooltip>
                    )}
                    {dropdownTabs.map((optionTab) => (
                      <PlanLockTooltip
                        key={optionTab}
                        locked={isDisabled(optionTab)}
                        feature={tabLabel(optionTab)}
                        plan={planFor(ORDER_TAB_ALGO[optionTab])}
                        className="flex flex-col"
                      >
                        <motion.button
                          type="button"
                          onClick={() => {
                            const tutorial = TAB_TUTORIAL[optionTab]
                            if (isDisabled(optionTab) && tutorial) return previewLocked(tutorial)
                            if (isDisabled(optionTab)) return
                            setDropdownOpen(false)
                            selectTab(optionTab)
                          }}
                          className={cn(
                            WIDGET_MENU_ROW_CLASS,
                            isDisabled(optionTab) && LOCKED_ITEM
                          )}
                          whileTap={{ scale: 0.98 }}
                          transition={{ duration: 0.15 }}
                        >
                          <WidgetMenuRowContent
                            icon={(() => {
                              const Icon = TAB_DETAILS[optionTab].icon
                              return <Icon size={18} />
                            })()}
                            label={
                              <>
                                {tabLabel(optionTab)}
                                {isDisabled(optionTab) && <PlanStar />}
                              </>
                            }
                            description={descriptions(TAB_DETAILS[optionTab].description)}
                            trailing={
                              !formContext.isSpreaderMode && selectedTab === optionTab ? (
                                <CheckIcon size={14} className="shrink-0" />
                              ) : null
                            }
                          />
                        </motion.button>
                      </PlanLockTooltip>
                    ))}
                    {/* <Separator /> */}
                    {/* Hedge isn't an order type — it opens the hedge builder dialog directly. */}
                    {/* <motion.button
                    type="button"
                    onClick={() => {
                      openHedgeDialog()
                      setDropdownOpen(false)
                    }}
                    className={WIDGET_MENU_ROW_CLASS}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                  >
                    <WidgetMenuRowContent
                      icon={<ScaleIcon size={18} />}
                      label={t('hedgeBuilder')}
                      description={descriptions('hedge')}
                    />
                  </motion.button> */}
                    {/* <PlanLockTooltip
                    locked={!isArbAllowed}
                    feature={t('arbBuilder')}
                    plan={planFor(AlgoType.ALGO_TYPE_CROSS_EXCHANGE_ARB)}
                    className="flex flex-col"
                  >
                    <motion.button
                      type="button"
                      onClick={() => {
                        if (!isArbAllowed) return previewLocked('arb')
                        openArbDialog(
                          arbSeedLegs.length > 0
                            ? { leg1: arbSeedLegs[0], leg2: arbSeedLegs[1] }
                            : undefined
                        )
                        setDropdownOpen(false)
                      }}
                      className={cn(WIDGET_MENU_ROW_CLASS, !isArbAllowed && LOCKED_ITEM)}
                      whileTap={{ scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                    >
                      <WidgetMenuRowContent
                        icon={<TimerIcon size={18} />}
                        label={
                          <>
                            {t('arbBuilder')}
                            {!isArbAllowed && <PlanStar />}
                          </>
                        }
                        description={descriptions('arb')}
                      />
                    </motion.button>
                  </PlanLockTooltip> */}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </TabsList>
        </Tabs>
      </div>
    </PromptFillFlash>
  )
}
